import type { FuzzyBounds, FuzzyValue } from '../util/fuzzyNumbers';
import { addFuzzy, integerToFuzzy, fractionToFuzzy, multiplyFuzzy, divideFuzzy, subtractFuzzy, compareFuzzy, maxFuzzy } from '../util/fuzzyMath';
import type { TamsClient } from '../types/tams';
import type { TamsReference } from '../dto/tams';
import type { TamsSegment, TamsHttpRequest } from './schemas';
import type { Logger } from '../types/logger';
import { invariant } from '../util/invariant';
import { frac } from '../util/integerMath';

/**
 * The TAMS Source a reference points at. A source reference names it directly;
 * a flow reference reaches it through any of its flows (they all belong to the
 * same source). `null` for an empty flow reference or a dangling flow id.
 */
export async function tamsSourceIdForReference(tamsClient: TamsClient, reference: TamsReference): Promise<string | null> {
  if (reference.type === 'source') {
    return reference.sourceId;
  }
  const [flowId] = [...(reference.proxyFlowIds ?? []), ...(reference.highresFlowIds ?? [])];
  if (flowId === undefined) {
    return null;
  }
  const flow = await tamsClient.getFlow(flowId);
  return flow?.source_id ?? null;
}

export function parseTamsTimerange(timerange: string): FuzzyBounds {
  // [0:0_10:0)
  let startInclusive : boolean;
  const startChar = timerange[0];
  switch (startChar) {
    case '[':
      startInclusive = true;
      break;
    case '(':
      startInclusive = false;
      break;
    default:
      throw new Error(`Unexpected start-of-range character '${startChar}' in timerange '${timerange}'`);
  }

  let endInclusive : boolean;
  const endChar = timerange[timerange.length - 1];
  switch (endChar) {
    case ']':
      endInclusive = true;
      break;
    case ')':
      endInclusive = false;
      break;
    default:
      throw new Error(`Unexpected end-of-range character '${endChar}' in timerange '${timerange}'`);
  }

  const meat = timerange.slice(1, timerange.length - 1);
  const [startSpec, endSpec] = meat.split('_');

  invariant(startSpec && endSpec, `Bad timerange format: ${timerange}`);

  const parseSpec = (s: string): FuzzyValue => {
    const [seconds, nanoseconds] = s.split(':').map((part) => parseInt(part));
    invariant(seconds !== undefined && nanoseconds !== undefined, `Bad time spec: ${s}`);
    if (!isFinite(seconds) || !isFinite(nanoseconds)) {
      throw new Error(`Bad time spec: ${s}`);
    }
    return addFuzzy(integerToFuzzy(seconds), fractionToFuzzy(frac(nanoseconds, 1_000_000_000)));
  };

  let start = parseSpec(startSpec);
  let end = parseSpec(endSpec);
  const nanoSecond = fractionToFuzzy(frac(1, 1_000_000_000));

  // For FuzzyBounds start is assumed to be inclusive. If it isn't we need to
  // add a nano second to indicate this (I guess)
  if (!startInclusive) {
    start = addFuzzy(start, nanoSecond);
  }
  // For fuzzyBounds end is assumed to be exclusive. If it is inclusive we need
  // to add a nano second to turn it into the equivalent exclusive bound.
  if (endInclusive) {
    end = addFuzzy(end, nanoSecond);
  }

  return { start, end };
}

function serializeTamsTimerangeValue(value: FuzzyValue): string {
  // Float-based (double) serialization: only ~µs precision at epoch scale, and
  // Math.floor on both parts requires a non-negative value. Sufficient for the
  // fuzzy segment-level searching in this module; producers that need exact
  // nanosecond timeranges must use tamsTiming.ts instead.
  invariant(value.approximate >= 0, `Cannot serialize negative TAMS timestamp: ${value.approximate}`);
  const seconds = Math.floor(value.approximate);
  const nanoseconds = Math.floor((value.approximate - seconds) * 1_000_000_000);
  return `${seconds}:${nanoseconds}`;
}

export function serializeTamsTimerange(
  timerange: FuzzyBounds,
  start: 'inclusive' | 'exclusive',
  end: 'inclusive' | 'exclusive',
): string {
  return [
    (start === 'inclusive') ? '[' : '(',
    serializeTamsTimerangeValue(timerange.start),
    '_',
    serializeTamsTimerangeValue(timerange.end),
    (end === 'inclusive') ? ']' : ')',
  ].join('');
}

// Sized against the actual failure mode, not segment duration: at Unix-epoch
// magnitudes, FuzzyValue.approximate (a JS double) only reliably holds
// ~microsecond precision (observed rounding errors are tens to a few hundred
// nanoseconds), so 1ms is a ~1000x safety margin over that while staying
// comfortably below any real segment — even at 240fps, a single frame is
// ~4.17ms. Used both as serializeTamsPointQuery's range width, and (by
// callers that sit exactly on a boundary shared with a neighboring segment,
// e.g. tamsCmafSegmentSource's steady-state playback query) as a forward
// nudge past that boundary before probing a zero-width point.
export const SEGMENT_BOUNDARY_EPSILON = fractionToFuzzy({ numerator: 1, denominator: 1000 });

/**
 * A TAMS timerange query that finds the segment covering `position`, as a
 * genuine `[position, position + epsilon)` range rather than a zero-width
 * point. A zero-width point query is fragile regardless of where it's
 * placed: at Unix-epoch magnitudes `FuzzyValue.approximate` (a JS double)
 * only reliably holds ~microsecond precision, so it can miss the intended
 * segment's true boundary by tens of nanoseconds — fatal specifically at a
 * flow's very first or last segment, where there's no neighboring segment to
 * land in instead. A real (non-zero-width) range tolerates that rounding,
 * per the TAMS spec's own semantics for this endpoint: it returns segments
 * that "partially or wholly overlap" the queried timerange, not just an
 * exact hit.
 *
 * Only safe when `position` has no adjacent segment sharing its exact
 * boundary — a caller in that situation (e.g. "the end of the segment I just
 * played, which is also the start of the next one") should nudge forward by
 * {@link SEGMENT_BOUNDARY_EPSILON} first so the whole range lands inside the
 * intended segment, rather than calling this directly and risking an
 * ambiguous overlap with the previous one too.
 */
export function serializeTamsPointQuery(position: FuzzyValue): string {
  const end = addFuzzy(position, SEGMENT_BOUNDARY_EPSILON);
  return serializeTamsTimerange({ start: position, end }, 'inclusive', 'exclusive');
}

/**
 * Thrown when a TAMS store does not presign media URLs. Mimir supports only
 * stores that hand out self-authorizing (pre-signed) URLs for both reads and
 * writes, so media bytes flow client/producer↔store directly and the store's
 * OAuth token never leaves Mimir's backend. A store that instead returns
 * bearer-required object URLs is unsupported; we fail loudly rather than hand a
 * caller a URL it cannot fetch (read) or upload to (write) without the token.
 */
export class TamsUnsupportedStoreError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'TamsUnsupportedStoreError';
  }
}

/**
 * The pre-signed URL to fetch a Segment's media bytes from.
 * @throws {TamsUnsupportedStoreError} if the segment carries URLs but none are
 * pre-signed (a non-presigning, unsupported store).
 * @throws {Error} if the segment carries no URL at all.
 */
export function selectTamsSegmentUrl(segment: TamsSegment): string {
  const url = tamsSegmentUrlOrNull(segment);
  if (url === undefined) {
    throw new Error('TAMS segment has no URLs');
  }
  return url;
}

/**
 * The pre-signed URL for a segment, or `undefined` for a bare
 * registration / not-yet-uploaded segment (no `get_urls`), which callers skip
 * rather than failing the whole batch. A segment that DOES carry URLs but none
 * pre-signed is a non-presigning store: that throws {@link TamsUnsupportedStoreError}
 * instead of silently falling back to a bearer-required URL the caller cannot use.
 */
export function tamsSegmentUrlOrNull(segment: TamsSegment): string | undefined {
  const urls = segment.get_urls;
  if (urls === undefined || urls.length === 0) {
    return undefined;
  }
  const presigned = urls.find((entry) => entry.label.includes('presigned'));
  if (presigned === undefined) {
    throw new TamsUnsupportedStoreError(
      `TAMS segment ${segment.object_id} offers only non-presigned URLs (labels: ${urls.map((entry) => entry.label).join(', ')}); Mimir supports only stores that presign media URLs`,
    );
  }
  return presigned.url;
}

/**
 * Whether an upload target is self-authorizing (pre-signed): usable without
 * attaching the store's OAuth token because it carries signing parameters in
 * its query string or supplies its own request headers. A bare object URL is
 * not — it would require the token, which Mimir never gives a producer.
 */
export function isPresignedUploadTarget(putUrl: TamsHttpRequest): boolean {
  const hasSigningQuery = putUrl.url.includes('?');
  const hasOwnHeaders = putUrl.headers !== undefined && Object.keys(putUrl.headers).length > 0;
  return hasSigningQuery || hasOwnHeaders;
}

/**
 * The playable time bounds of a Flow: its declared `timerange` when present,
 * otherwise derived from its segments (first segment start, binary-searched
 * last segment end). `lastKnownRange` lets a refresh skip re-discovering the
 * start of a flow that only grows at its end. Throws when the flow does not
 * exist; returns an empty zero range when it has no segments yet.
 */
export async function findTamsFlowRange(
  logger: Logger,
  tamsClient: TamsClient,
  flowId: string,
  lastKnownRange: FuzzyBounds | null,
): Promise<FuzzyBounds> {
  const flow = await tamsClient.getFlow(flowId);
  if (flow === null) {
    throw new Error(`No such flow: ${flowId}`);
  }
  if (flow.timerange !== undefined) {
    return parseTamsTimerange(flow.timerange);
  }

  let start: FuzzyValue;
  if (lastKnownRange !== null) {
    start = lastKnownRange.start;
  } else {
    const [firstSegment] = await tamsClient.getSegments({ flowId, limit: 1 });
    if (firstSegment === undefined) {
      logger.warn(`TAMS flow ${flowId} has no segments. Treating its range as empty`);
      return { start: integerToFuzzy(0), end: integerToFuzzy(0) };
    }
    start = parseTamsTimerange(firstSegment.timerange).start;
  }

  const lastSegment =
    await tamsBinarySearchLastSegment(
      logger,
      tamsClient,
      flowId,
      lastKnownRange?.start ?? null,
      false,
    );

  if (lastSegment === null) {
    logger.warn(`TAMS flow ${flowId}: unable to find last segment via binary search. Treating its range as empty`);
    return { start: integerToFuzzy(0), end: integerToFuzzy(0) };
  }

  return { start, end: parseTamsTimerange(lastSegment.timerange).end };
}

/**
 * Fetch the flow's last segment (the live edge) via TAMS reverse-order paging in
 * a single query, or report that the store does not support it.
 *
 * A store that ignores `reverse_order` returns the listing in forward order, so
 * `limit: 2` would hand back the two *earliest* segments — mistaking the first
 * segment for the tail. We trust the fast path only when the result positively
 * confirms reverse ordering: two segments in strictly descending time order.
 * Anything else — zero or one segment (an ignoring store may also return a
 * single first segment, so a lone segment is not proof), or two in ascending
 * order — reports `'reverse-order-unsupported'` and the caller falls back to
 * forward paging (cheap for the tiny flows where this ambiguity arises).
 */
async function tamsLastSegmentViaReverseOrder(
  tamsClient: TamsClient,
  flowId: string,
  provideUrls: boolean,
): Promise<TamsSegment | 'reverse-order-unsupported'> {
  const segments = await tamsClient.getSegments({ flowId, limit: 2, reverse_order: true, accept_get_urls: '' });
  if (segments.length < 2) {
    return 'reverse-order-unsupported';
  }
  const first = parseTamsTimerange(segments[0]!.timerange).start;
  const second = parseTamsTimerange(segments[1]!.timerange).start;
  if (compareFuzzy(first, second) <= 0) {
    return 'reverse-order-unsupported';
  }
  const tail = segments[0]!;
  if (!provideUrls) {
    return tail;
  }
  // The detection query suppressed URLs (accept_get_urls: '') for speed; refetch
  // the tail by object id to obtain its get_urls, matching the binary-search path.
  const [withUrls] = await tamsClient.getSegments({ flowId, object_id: tail.object_id });
  invariant(withUrls !== undefined, `Segment with object id ${tail.object_id} no longer exists`);
  return withUrls;
}

export async function tamsBinarySearchLastSegment(
  logger: Logger,
  tamsClient: TamsClient,
  flowId: string,
  startTime: FuzzyValue | null,
  provideUrls: boolean,
): Promise<TamsSegment | null> {
  // Fast path: a store honoring reverse-order paging returns the tail directly,
  // in one query, regardless of `startTime` (the tail is the tail). Falls
  // through to the forward doubling+bisect below when the store ignores it.
  const viaReverse = await tamsLastSegmentViaReverseOrder(tamsClient, flowId, provideUrls);
  if (viaReverse !== 'reverse-order-unsupported') {
    return viaReverse;
  }
  logger.info(`TAMS flow ${flowId}: store did not honor reverse-order paging; binary-searching for the end segment`);

  let high : FuzzyValue;
  if (startTime === null) {
    const [firstSegment] = await tamsClient.getSegments({ flowId, limit: 1 });
    if (firstSegment === undefined) {
      return null;
    }
    const range = parseTamsTimerange(firstSegment.timerange);
    high = range.end;
  } else {
    high = startTime;
  }

  let low : FuzzyValue = integerToFuzzy(0);
  let jump : FuzzyValue = integerToFuzzy(0);

  let segmentDuration : FuzzyValue = integerToFuzzy(1);

  let endSegment : TamsSegment | null = null;

  // 1. Double high until no segments
  // 2. Keep last position that resulted in segment in low
  while (true) {
    logger.debug(`Checking position ${high.approximate.toFixed(2)}`);

    const timerange = serializeTamsPointQuery(high);

    const segments = await tamsClient.getSegments({ flowId, timerange, accept_get_urls: '' });

    if (segments.length === 0) {
      logger.info(`Went past end at ${high.approximate.toFixed(2)}`);
      break;
    }

    endSegment = segments[0]!;

    const segmentRange = parseTamsTimerange(segments[0]!.timerange);
    segmentDuration = subtractFuzzy(segmentRange.end, segmentRange.start);

    low = high;
    jump = maxFuzzy(multiplyFuzzy(jump, integerToFuzzy(2)), segmentDuration);
    logger.debug(`Not gone past end yet. Will increase current position by ${jump.approximate.toFixed(2)}`);
    high = addFuzzy(high, jump);
  }

  // 3. Binary search to find the last segment between low and high
  while (true) {
    const middle = divideFuzzy(addFuzzy(high, low), integerToFuzzy(2));

    const timerange = serializeTamsPointQuery(middle);

    const segments = await tamsClient.getSegments({ flowId, timerange, accept_get_urls: '' });

    if (compareFuzzy(segmentDuration, subtractFuzzy(high, low)) > 0) {
      // The terminating probe may have landed past the last segment; fall back
      // to the last segment any earlier probe found.
      const found = segments[0] ?? endSegment;
      if (found === null) {
        return null;
      }
      if (provideUrls) {
        const [segment] = await tamsClient.getSegments({ flowId, object_id: found.object_id });
        invariant(segment !== undefined, `Segment with object id ${found.object_id} no longer exists`);
        return segment;
      }
      return found;
    }

    if (segments.length === 0) {
      high = middle;
      logger.info(`Still after end at ${high.approximate.toFixed(2)}`);
      continue;
    }

    endSegment = segments[0]!;

    low = middle;
    logger.info(`Found segment but search interval is still to large [${low.approximate.toFixed(2)}, ${high.approximate.toFixed(2)}>`);
  }
}

